import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { MatSnackBar } from '@angular/material/snack-bar';
import { SelectEvent } from '@progress/kendo-angular-upload';
import { firstValueFrom, forkJoin, Subject } from 'rxjs';
import { v4 as UUID } from 'uuid';

import { App, Color, GeoJsonFile, KhFile, KHFileInfo, MapContextLayer, MappedMapElementColorSelection, PresetStyleColorSelection, Project } from '../../models';
import { HEX_BY_CONTEXT_LAYER_COLOR_CONSTANTS } from '../../shared/constants/hex-by-context-layer-color';
import { FileTypes, MapElementColor, SidesheetComponentOptions } from '../../shared/enums';
import { FileDataService, MapDataService, ShapefileDataService, UploadDataService } from '../data-services';
import { ColorsUtilityService, SidesheetUtilityService } from '../utility-services';
import { ColorObjectByContextLayerColor, MapContextLayersLogicServiceModel } from './models';

@Injectable({
    providedIn: 'root'
})
export class MapContextLayersLogicService {
    private _geoJsonFeaturesLoaded = new Subject<boolean>();
    private _contextLayersChanged = new Subject<boolean>();
    private _geoPreviewJsonFeaturesChanged = new Subject<boolean>();
    private _modelInitialized = new Subject<boolean>();

    geoJsonFeaturesLoaded$ = this._geoJsonFeaturesLoaded.asObservable();
    contextLayersChanged$ = this._contextLayersChanged.asObservable();
    geoPreviewJsonFeaturesChanged$ = this._geoPreviewJsonFeaturesChanged.asObservable();
    modelInitialized$ = this._modelInitialized.asObservable();

    model = new MapContextLayersLogicServiceModel();

    constructor(
        private _colorsUtilityService: ColorsUtilityService,
        private _fileDataService: FileDataService,
        private _mapDataService: MapDataService,
        private _shapefileDataService: ShapefileDataService,
        private _matSnackBar: MatSnackBar,
        private _sidesheetUtilityService: SidesheetUtilityService,
        private _uploadDataService: UploadDataService
    ) { }

    initModel(): void {
        this.model = new MapContextLayersLogicServiceModel();
        this.model.colorObjectByContextLayerColor = this.createContextColorsAsColorObjects();
        this._modelInitialized.next(true);
    }

    initFormOptions(): void {
        this.model.isInEditMode = false;
        this.model.fileUploadProperties.shapefile = new KHFileInfo();
        this.model.fileUploadProperties.selectedShapefiles = [];
        this.setAvailableColors();
        this.initForm();
    }

    createContextColorsAsColorObjects(): ColorObjectByContextLayerColor {
        return {
            [MapElementColor.Red]: this.convertContextLayerColorToColorObject(MapElementColor.Red),
            [MapElementColor.Green]: this.convertContextLayerColorToColorObject(MapElementColor.Green),
            [MapElementColor.Blue]: this.convertContextLayerColorToColorObject(MapElementColor.Blue),
            [MapElementColor.Yellow]: this.convertContextLayerColorToColorObject(MapElementColor.Yellow),
            [MapElementColor.Orange]: this.convertContextLayerColorToColorObject(MapElementColor.Orange),
            [MapElementColor.Purple]: this.convertContextLayerColorToColorObject(MapElementColor.Purple),
            [MapElementColor.Black]: this.convertContextLayerColorToColorObject(MapElementColor.Black),
            [MapElementColor.Aqua]: this.convertContextLayerColorToColorObject(MapElementColor.Aqua),
            [MapElementColor.White]: this.convertContextLayerColorToColorObject(MapElementColor.White),
            [MapElementColor.Gray]: this.convertContextLayerColorToColorObject(MapElementColor.Gray),
            [MapElementColor.NoColor]: this.convertContextLayerColorToColorObject(MapElementColor.NoColor),
        };
    }

    convertContextLayerColorToColorObject(contextLayerColor: MapElementColor): Color {
        const hexString = HEX_BY_CONTEXT_LAYER_COLOR_CONSTANTS[contextLayerColor];
        return this._colorsUtilityService.hexToColorObject(hexString);
    }

    shapeFileSelected(app: App, project: Project, fileSelectEvent: SelectEvent): void {
        this.initializeShapefileSelectedState();

        if (!fileSelectEvent.files[0]) {
            this.handleNoFilesSelected();
            return;
        }

        this.model.fileUploadProperties.selectedShapefiles = [...fileSelectEvent.files];
        this.model.fileUploadProperties.shapefile = this.model.fileUploadProperties.selectedShapefiles[0];
        if (this.model.isInEditMode) {
            this.model.editedLayerUploadChanged = true;
        }
        this.getGeoJsonFileFromShapefile(app, project);
    }

    shapefileRemoved(): void {
        this.model.fileUploadProperties.selectedShapefiles = [];
        this.model.fileUploadProperties.shapefile = undefined;
        this.model.geoJsonFile = new GeoJsonFile();
        this.model.form.get('geoJsonFile').setValue(null);
        this.model.fileUploadProperties.showErrorMessage = false;
        this.emitGeoPreviewJsonFeaturesChanged();
    }

    initializeShapefileSelectedState(): void {
        this.model.fileUploadProperties.isFetchingGeoJsonFile = true;
        this.model.fileUploadProperties.showErrorMessage = false;
    }

    handleNoFilesSelected(): void {
        this.model.fileUploadProperties.selectedShapefiles = [];
        this.model.fileUploadProperties.shapefile = undefined;
        this.model.fileUploadProperties.showErrorMessage = true;
    }

    getMapContextLayersForAppAndLoadGeoJsonFeatures(app: App, project: Project): void {
        if (!project.id) {
            return;
        }

        this.model.fetchingAllLayers = true;
        this._mapDataService.getMapContextLayersForApp(app)
            .subscribe({
                next: (contextLayers: MapContextLayer[]) => {
                    this.model.contextLayers = contextLayers;
                    this.model.hasLoadedAllLayers = true;
                    this._contextLayersChanged.next(true);
                    if (contextLayers.length > 0) {
                        const contextLayersURIs = this.model.contextLayers.filter(layer => layer.geoJsonFeatures === undefined).map(layer => layer.geoJsonUri);
                        this.downloadContextLayersGeoJsonFiles(app, project, contextLayersURIs);
                    }
                    this._matSnackBar.open('Context layers loaded', 'Close', { duration: 3000 });
                    this.model.fetchingAllLayers = false;
                },
                error: () => {
                    this._matSnackBar.open('Error loading context layers', 'Close', { duration: 3000 });
                }
            });
    }

    getAllLayersForAppLoadGeoJsonFeaturesAllProjectsAndProject(app: App, project: Project): void {
        if (!project.id) {
            return;
        }
        this.model.fetchingAllLayers = true;
        this._mapDataService.getMapContextLayersForApp(app)
            .subscribe({
                next: (contextLayers: MapContextLayer[]) => {
                    this.model.contextLayers = contextLayers;
                    this.model.hasLoadedAllLayers = true;
                    this._contextLayersChanged.next(true);
                    if (contextLayers.length > 0) {
                        const contextLayersURIs = this.model.contextLayers.filter(layer => layer.selectedProjectIdList.includes("-1") || layer.selectedProjectIdList.includes(project.id)).map(layer => layer.geoJsonUri);
                        this.downloadContextLayersGeoJsonFiles(app, project, contextLayersURIs);
                    }
                    this._matSnackBar.open('Context layers loaded', 'Close', { duration: 3000 });
                    this.model.fetchingAllLayers = false;
                },
                error: () => {
                    this._matSnackBar.open('Error loading context layers', 'Close', { duration: 3000 });
                }
            });
    }

    getUnloadedProjectGeoJsonFeatures(app: App, project: Project): void {
        if (!this.model.hasLoadedAllLayers) {
            return;
        }
        const contextLayersURIs = this.model.contextLayers.filter(layer => layer.geoJsonFeatures === undefined && layer.selectedProjectIdList.includes(project.id)).map(layer => layer.geoJsonUri);
        this.downloadContextLayersGeoJsonFiles(app, project, contextLayersURIs);
    }

    downloadContextLayersGeoJsonFiles(app: App, project: Project, contextLayersURIs: string[]): void {
        const uploadPromises = contextLayersURIs.map(uri => {
            const uniqueFileName = this.getUniqueFileNameFromUri(uri);
            const download = project.isSummaryProject
                ? this._fileDataService.downloadSummaryProjectFileBlob(app, project, uniqueFileName)
                : this._fileDataService.downloadFileBlob(app, project, uniqueFileName);

            return firstValueFrom(download)
                .then((downloadedBlob: any) => this.populateGeoJsonFeatures(uri, downloadedBlob));
        });
        Promise.all(uploadPromises)
            .then(() => {
                this._geoJsonFeaturesLoaded.next(true);
            })
            .catch((error) => {
                throw new Error(error);
            });
    }

    getUniqueFileNameFromUri(uri: string): string {
        const uriArray = uri.split('/');
        return uriArray[uriArray.length - 1];
    }

    async populateGeoJsonFeatures(uri: string, downloadedBlob: any): Promise<void> {
        const contextLayer = this.model.contextLayers.find(layer => layer.geoJsonUri === uri);
        if (contextLayer) {
            const text = await downloadedBlob.text();
            contextLayer.geoJsonFeatures = JSON.parse(text).features;
        }
    }

    getGeoJsonFileFromShapefile(app: App, project: Project): void {
        const formData = new FormData();
        formData.append('file', this.model.fileUploadProperties.shapefile.rawFile);

        this._shapefileDataService.getSitesGeoJsonFile(app, project, formData)
            .subscribe({
                next: (base64File: string) => {
                    try {
                        this.model.geoJsonFile = JSON.parse(atob(base64File));
                        this.model.form.get('geoJsonFile').setValue(this.model.geoJsonFile);
                        this.emitGeoPreviewJsonFeaturesChanged();
                    }
                    catch (error) {
                        this.model.fileUploadProperties.showErrorMessage = true;
                    }
                },
                error: () => {
                    this.model.fileUploadProperties.showErrorMessage = true;
                },
                complete: () => {
                    this.model.fileUploadProperties.isFetchingGeoJsonFile = false;
                },
            });
    }

    saveEditedContextLayer(currentApp: App): void {
        this.model.isFormSaving = true;
        if (this.model.editedLayerUploadChanged) {
            this.deletePreviousGeoJsonFileAndUpdateContextLayer(currentApp);
            return;
        }
        this.updateContextLayer(currentApp);
    }

    deletePreviousGeoJsonFileAndUpdateContextLayer(currentApp: App): void {
        const geoJsonFileToDelete = this.toBeDeletedGeoJsonFile(this.model.contextLayerEditing);
        const shapeFileToDelete = this.toBeDeletedShapeFile(this.model.contextLayerEditing);

        const deleteOperations: any = {
            geoJsonFile: this._uploadDataService.removeFile(currentApp, geoJsonFileToDelete)
        };

        if (shapeFileToDelete) {
            deleteOperations.shapefile = this._uploadDataService.removeFile(currentApp, shapeFileToDelete);
        }

        forkJoin(deleteOperations).subscribe({
            next: () => {
                this.uploadGeoJsonFileForEditedContextLayer(currentApp);
            },
            error: () => {
                this.model.fileUploadProperties.showErrorMessage = true;
            }
        });
    }

    toBeDeletedGeoJsonFile(layer: MapContextLayer): KhFile {
        const fileEntry = new KhFile();
        const fileFromGeoJsonInfo = new KHFileInfo();
        fileFromGeoJsonInfo.extension = `.${FileTypes.GeoJson}`;
        fileFromGeoJsonInfo.uri = layer.geoJsonUri;
        fileFromGeoJsonInfo.name = layer.geoJsonfileName;
        fileEntry.id = UUID();
        fileEntry.file = fileFromGeoJsonInfo;
        fileEntry.containerType = "private";
        return fileEntry;
    }

    toBeDeletedShapeFile(layer: MapContextLayer): KhFile | null {
        if (!layer.shapeFileUri) {
            return null;
        }
        const fileEntry = new KhFile();
        const fileFromShapeFileInfo = new KHFileInfo();
        fileFromShapeFileInfo.uri = layer.shapeFileUri;
        fileFromShapeFileInfo.name = layer.shapeFileName;
        fileEntry.id = UUID();
        fileEntry.file = fileFromShapeFileInfo;
        fileEntry.containerType = "private";
        return fileEntry;
    }

    uploadGeoJsonFileForEditedContextLayer(app: App): void {
        const fileNameWithoutExtension = this.model.fileUploadProperties.shapefile.name.slice(0, this.model.fileUploadProperties.shapefile.name.lastIndexOf('.'));
        const geoJsonFormData = new FormData();
        const shapefileFormData = new FormData();
        const geoJsonFileBlob = new File([JSON.stringify(this.model.geoJsonFile)], `${fileNameWithoutExtension}.${FileTypes.GeoJson}`);

        geoJsonFormData.append('files', geoJsonFileBlob);
        shapefileFormData.append('files', this.model.fileUploadProperties.shapefile.rawFile);

        forkJoin({
            geoJsonFile: this._uploadDataService.uploadFile(app, geoJsonFormData),
            shapefile: this._uploadDataService.uploadFile(app, shapefileFormData)
        }).subscribe({
            next: (uploadResults) => {
                this.updateContextLayer(app, uploadResults.geoJsonFile, uploadResults.shapefile);
            },
            error: () => {
                this.model.fileUploadProperties.showErrorMessage = true;
            }
        });
    }

    uploadGeoJsonFileForContextLayer(app: App): void {
        this.model.isFormSaving = true;
        const fileNameWithoutExtension = this.model.fileUploadProperties.shapefile.name.slice(0, this.model.fileUploadProperties.shapefile.name.lastIndexOf('.'));
        const geoJsonFormData = new FormData();
        const shapefileFormData = new FormData();
        const geoJsonFileBlob = new File([JSON.stringify(this.model.geoJsonFile)], `${fileNameWithoutExtension}.${FileTypes.GeoJson}`);

        geoJsonFormData.append('files', geoJsonFileBlob);
        shapefileFormData.append('files', this.model.fileUploadProperties.shapefile.rawFile);

        forkJoin({
            geoJsonFile: this._uploadDataService.uploadFile(app, geoJsonFormData),
            shapefile: this._uploadDataService.uploadFile(app, shapefileFormData)
        }).subscribe({
            next: (uploadResults) => {
                this.createMapContextLayer(app, uploadResults.geoJsonFile, uploadResults.shapefile);
            },
            error: () => {
                this.model.fileUploadProperties.showErrorMessage = true;
                this.model.isFormSaving = false;
            }
        });
    }

    updateContextLayer(app: App, geoJsonFileInfo?: KHFileInfo, shapefileInfo?: KHFileInfo): void {
        const contextLayerToUpdate = this.updateExistingContextLayer(geoJsonFileInfo, shapefileInfo);
        this._mapDataService.updateContextLayer(app, contextLayerToUpdate)
            .subscribe({
                next: (updatedLayer: MapContextLayer) => {
                    updatedLayer.geoJsonFeatures = this.model.geoJsonFile.features;
                    const outdatedLayerIndex = this.model.contextLayers.findIndex(layer => layer.id === updatedLayer.id);
                    this.model.contextLayers[outdatedLayerIndex] = updatedLayer;
                    this._contextLayersChanged.next(true);
                    this.model.isFormSaving = false;
                    this._sidesheetUtilityService.closeSidesheet();
                    this._matSnackBar.open('Context layer updated', 'Close', { duration: 3000 });
                },
                error: () => {
                    this._matSnackBar.open('Error updating context layer', 'Close', { duration: 3000 });
                    this.model.isFormSaving = false;
                }
            });
    }

    createMapContextLayer(app: App, uploadedGeoJsonProperties: KHFileInfo, uploadedShapefileProperties?: KHFileInfo): void {
        const contextLayerToCreate = this.createMapContextLayerFromForm(uploadedGeoJsonProperties, uploadedShapefileProperties);
        this._mapDataService.createMapContextLayer(app, contextLayerToCreate)
            .subscribe({
                next: (mapContextLayer: MapContextLayer) => {
                    mapContextLayer.geoJsonFeatures = this.model.geoJsonFile.features;
                    this.model.contextLayers.push(mapContextLayer);
                    this._contextLayersChanged.next(true);
                    this.model.isFormSaving = false;
                    this._sidesheetUtilityService.closeSidesheet();
                    this._matSnackBar.open('Context layer added', 'Close', { duration: 3000 });
                },
                error: () => {
                    this._matSnackBar.open('Error adding context layer', 'Close', { duration: 3000 });
                    this.model.isFormSaving = false;
                }
            });
    }

    createMapContextLayerFromForm(uploadedGeoJsonProperties: KHFileInfo, uploadedShapefileProperties?: KHFileInfo): MapContextLayer {
        const contextLayer = new MapContextLayer();
        contextLayer.name = this.model.form.get('layerName').value;
        contextLayer.description = this.model.form.get('layerDescription').value;
        contextLayer.selectedProjectIdList = this.model.form.get('projects').value;
        contextLayer.strokeWidth = this.model.form.get('strokeWidth').value;
        contextLayer.strokeColor = this.model.form.get('strokeColor').value;
        contextLayer.fillColor = this.model.form.get('fillColor').value;
        contextLayer.fillOpacity = this.model.form.get('fillOpacity').value;
        contextLayer.shapeFileName = this.model.fileUploadProperties.shapefile.name;
        contextLayer.shapeFileSize = this.model.fileUploadProperties.shapefile.size;
        contextLayer.shapeFileUri = uploadedShapefileProperties?.uri || '';
        contextLayer.geoJsonfileName = uploadedGeoJsonProperties.name;
        contextLayer.geoJsonUri = uploadedGeoJsonProperties.uri;
        contextLayer.order = this.model.contextLayers.length + 1;

        return contextLayer;
    }

    updateExistingContextLayer(geoJsonFileInfo?: KHFileInfo, shapefileInfo?: KHFileInfo): MapContextLayer {
        const contextLayerToUpdate = { ...this.model.contextLayerEditing };
        contextLayerToUpdate.name = this.model.form.get('layerName').value;
        contextLayerToUpdate.description = this.model.form.get('layerDescription').value;
        contextLayerToUpdate.selectedProjectIdList = this.model.form.get('projects').value;
        contextLayerToUpdate.strokeWidth = this.model.form.get('strokeWidth').value;
        contextLayerToUpdate.strokeColor = this.model.form.get('strokeColor').value;
        contextLayerToUpdate.fillColor = this.model.form.get('fillColor').value;
        contextLayerToUpdate.fillOpacity = this.model.form.get('fillOpacity').value;
        contextLayerToUpdate.shapeFileName = this.model.fileUploadProperties.shapefile.name;
        contextLayerToUpdate.shapeFileSize = this.model.fileUploadProperties.shapefile.size;
        if (geoJsonFileInfo) {
            contextLayerToUpdate.geoJsonfileName = geoJsonFileInfo.name;
            contextLayerToUpdate.geoJsonUri = geoJsonFileInfo.uri;
        }
        if (shapefileInfo) {
            contextLayerToUpdate.shapeFileUri = shapefileInfo.uri;
        }

        return contextLayerToUpdate;
    }

    private setAvailableColors(): void {
        this.model.availableStrokeColors = [];
        this._colorsUtilityService.model.mapElementColors.forEach((fieldStyleColor: MappedMapElementColorSelection, key: number) => {
            const mapContextLayerStrokeColor: PresetStyleColorSelection = new PresetStyleColorSelection();
            mapContextLayerStrokeColor.class = fieldStyleColor.class;
            mapContextLayerStrokeColor.name = fieldStyleColor.name;
            mapContextLayerStrokeColor.id = key;
            this.model.availableStrokeColors.push(mapContextLayerStrokeColor);
        });
        this.model.availableFillColors = [];
        this._colorsUtilityService.model.mapContextLayersFillColors.forEach((fieldStyleColor: MappedMapElementColorSelection, key: number) => {
            const mapContextLayerFillColor: PresetStyleColorSelection = new PresetStyleColorSelection();
            mapContextLayerFillColor.class = fieldStyleColor.class;
            mapContextLayerFillColor.name = fieldStyleColor.name;
            mapContextLayerFillColor.id = key;
            this.model.availableFillColors.push(mapContextLayerFillColor);
        });
    }

    private initForm(): void {
        this.model.form = new UntypedFormGroup({
            layerName: new UntypedFormControl('', [Validators.required, Validators.maxLength(75)]),
            layerDescription: new UntypedFormControl('', [Validators.maxLength(200)]),
            projects: new UntypedFormControl([]),
            strokeWidth: new UntypedFormControl(2, [Validators.required, Validators.min(1), Validators.max(10)]),
            strokeColor: new UntypedFormControl(MapElementColor.Red, [Validators.required]),
            fillColor: new UntypedFormControl(MapElementColor.Red, [Validators.required]),
            fillOpacity: new UntypedFormControl(0.8, [Validators.required, Validators.min(0.1), Validators.max(1)]),
            geoJsonFile: new UntypedFormControl(null, [Validators.required]),
        });
    }

    editLayer(layer: MapContextLayer): void {
        this.model.isInEditMode = true;
        this.model.contextLayerEditing = layer;
        this.setAvailableColors();
        this.initForm();
        this.model.form.patchValue({
            layerName: layer.name,
            layerDescription: layer.description,
            projects: layer.selectedProjectIdList,
            strokeWidth: layer.strokeWidth,
            strokeColor: layer.strokeColor,
            fillColor: layer.fillColor,
            fillOpacity: layer.fillOpacity,
            geoJsonFile: layer.geoJsonUri,
        });
        this.model.geoJsonFile = new GeoJsonFile();
        this.model.geoJsonFile.features = layer.geoJsonFeatures;
        this.model.fileUploadProperties.shapefile = new KHFileInfo();
        this.model.fileUploadProperties.shapefile.name = layer.shapeFileName;
        this.model.fileUploadProperties.shapefile.size = layer.shapeFileSize;
        this.model.fileUploadProperties.shapefile.uri = layer.shapeFileUri;
        this.model.fileUploadProperties.selectedShapefiles = [this.model.fileUploadProperties.shapefile];

        this._sidesheetUtilityService.openSidesheet(SidesheetComponentOptions.MapContextLayerForm);
        this._geoPreviewJsonFeaturesChanged.next(true);
    }

    deleteContextLayer(currentApp: App, layerToDelete: MapContextLayer): void {
        this._mapDataService.deleteContextLayer(currentApp, layerToDelete).subscribe({
            next: () => {
                this.model.contextLayers = this.model.contextLayers.filter(layer => layer.id !== layerToDelete.id);
                this._contextLayersChanged.next(true);
                this._geoPreviewJsonFeaturesChanged.next(true);
                this._matSnackBar.open('Context layer deleted successfully', 'Close', { duration: 3000 });
            },
            error: () => {
                this._matSnackBar.open('Error deleting context layer', 'Close', { duration: 3000 });
            }
        });
    }

    updateContextLayerOrders(app: App, updateContextLayerOrdersDto: Record<string, number>): void {
        this._mapDataService.updateContextLayerOrders(app, updateContextLayerOrdersDto).subscribe({
            next: (response) => {
                this._matSnackBar.open('Context layers reordered successfully', 'Close', { duration: 3000 });
                this.updateModelContextLayerOrders(response);
                this._geoPreviewJsonFeaturesChanged.next(true);
            },
            error: () => {
                this._matSnackBar.open('Error reordering context layers', 'Close', { duration: 3000 });
                this._contextLayersChanged.next(true);
            }
        });
    }

    updateModelContextLayerOrders(updatedContextLayers: MapContextLayer[]): void {
        updatedContextLayers.forEach(updatedLayer => {
            const existingLayer = this.model.contextLayers.find(l => l.id === updatedLayer.id);
            if (existingLayer) {
                existingLayer.order = updatedLayer.order;
            }
        });
        this._contextLayersChanged.next(true);
    }

    emitGeoPreviewJsonFeaturesChanged(): void {
        this._geoPreviewJsonFeaturesChanged.next(true);
    }

    getColorClassOfContextLayer(contextLayer: MapContextLayer): { [key: string]: string; } {
        if (!this.model.colorObjectByContextLayerColor) {
            return {};
        }

        const rgb = this.model.colorObjectByContextLayerColor[contextLayer.fillColor];

        if (!rgb) {
            return {
                'border': `2px solid`,
                'border-color': HEX_BY_CONTEXT_LAYER_COLOR_CONSTANTS[contextLayer.strokeColor],
            };
        }

        const backgroundColor = new Color(rgb.r, rgb.g, rgb.b, contextLayer.fillOpacity);

        return {
            'background-color': backgroundColor.rgba,
            'border': `2px solid`,
            'border-color': HEX_BY_CONTEXT_LAYER_COLOR_CONSTANTS[contextLayer.strokeColor],
        };
    }
}
